import { Component, Inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { ApiUrlHelper } from '../../common/api-url-helper';
import { CommonService } from '../../services/common/common.service';
import { SharedService } from '../../services/shared-service/shared.service';
import { CommonErrorMessages } from '../../constants/ErrorMessages';

@Component({
  selector: 'app-user-details',
  standalone: false,
  templateUrl: './user-details.html',
  styleUrl: './user-details.scss'
})

export class UserDetails {

  userForm!: FormGroup;
  submitted: boolean = false;
  userLabelConstants: any;
  commonConstants: any;
  commonErrors: any;
  isPreviewTrue: boolean = false;
  userId: any = 0;
  loggedInUserId: any;
  userImage: any;
  imageSrc: string;
  editedUserDetails: any;
  decryptUserId: any;
  deleteImage: boolean = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private readonly dialogRef: MatDialogRef<UserDetails>,
    private readonly fb: FormBuilder,
    private readonly api: ApiUrlHelper,
    private readonly toastr: ToastrService,
    private readonly spinner: NgxSpinnerService,
    private readonly commonService: CommonService,
    private sharedService: SharedService
  ) {
  }

  ngOnInit(): void {
    this.createUserDetailsForm();
    this.loggedInUserId = this.sharedService.getValue('userId');
    if (this.loggedInUserId != null && this.loggedInUserId != undefined && this.loggedInUserId != '' && this.loggedInUserId != 'undefined') {
      this.decryptUserId = this.commonService.Decrypt(this.loggedInUserId);
      this.userId = this.decryptUserId;
      this.loggedInUserId = this.decryptUserId;
    }
    this.getUserById();
  }

  get userFormControls() {
    return this.userForm.controls;
  }

  createUserDetailsForm() {
    this.userForm = this.fb.group({
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      userEmail: ['', [Validators.required, Validators.email, Validators.pattern(/^(?=.{1,30}@)[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/)]],
      address: [''],
      phone: ['', Validators.minLength(12)],
    });
  }

  onFileSelected(event: any): void {
    const file = event.target.files[0];
    if (file) {
      const fileSizeInBytes = file.size;
      const maxSizeInBytes = 10 * 1024 * 1024; // 10MB

      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
      const allowedExtensions = ['jpg', 'jpeg', 'png'];

      const fileType = file.type;
      const fileExtension = file.name.split('.').pop()?.toLowerCase();

      // Check MIME type and extension
      if (!allowedTypes.includes(fileType) || !allowedExtensions.includes(fileExtension || '')) {
        this.toastr.error(CommonErrorMessages.ImagesAllowed);
        return;
      }

      if (fileSizeInBytes > maxSizeInBytes) {
        this.toastr.error('Selected file exceeds the maximum allowed size (10MB).');
        return;
      }

      this.userImage = file;
      this.deleteImage = false; //  Reset flag if image is selected

      const reader = new FileReader();
      reader.onload = (e: any) => {
        this.imageSrc = e.target.result;
      };
      reader.readAsDataURL(file);
    }
  }

  submitUserForm() {
    this.submitted = true;
    if (this.userForm.valid) {
      this.userForm.controls['userEmail'].enable();

      const formData = new FormData();
      formData.append('AdminUserId', this.userId.toString());
      formData.append('FirstName', this.userForm.value.firstName.trim());
      formData.append('LastName', this.userForm.value.lastName.trim());
      formData.append('Email', this.userForm.value.userEmail.trim());
      formData.append('PhoneNo', this.userForm.value.phone.trim());
      formData.append('Address', this.userForm.value.address.trim());
      if (this.userImage) {
        formData.append('ProfilePicture', this.userImage);
      }

      //  Send DeleteImage flag explicitly
      formData.append('DeleteImage', this.deleteImage ? 'true' : 'false');
      let api = this.api.apiUrl.User.SaveUser;
      this.spinner.show();
      this.commonService.doPost(api, formData).pipe().subscribe({
        next: (response) => {
          if (response.success) {
            this.toastr.success(response.message);
            this.spinner.hide();

            if (this.userId == this.loggedInUserId) {

              this.sharedService.setValue(
                'fullName',
                this.commonService.Encrypt(
                  `${this.userForm.value.firstName.trim()} ${this.userForm.value.lastName.trim()}`
                )
              );

              if (this.userImage) {
                this.sharedService.setValue(
                  'profilePicture',
                  this.commonService.Encrypt(this.imageSrc)
                );
              } else if (this.deleteImage) {
                this.sharedService.setValue('profilePicture', '');
              }

              this.commonService.setUserName(
                `${this.userForm.value.firstName.trim()} ${this.userForm.value.lastName.trim()}`
              );
              this.commonService.setUserImage(this.imageSrc);
            }
            this.dialogRef.close(true);
          } else {
            if (this.userId > 0) {
              this.userForm.controls['userEmail'].disable();
            }
            this.toastr.error(response.message);
            this.spinner.hide();
          }
        },
        error: (err) => {
          if (this.userId > 0) {
            this.userForm.controls['userEmail'].disable();
          }
          this.spinner.hide();
          console.error(err);
        },
      });
    }
  }

  removeImage(): void {
    this.userImage = null;
    this.imageSrc = null;
    this.deleteImage = true; //  Signal backend to remove image
  }

  getUserById() {
    this.spinner.show();
    let api = this.api.apiUrl.User.GetUserById.replace('{adminUserId}', this.userId);
    this.commonService.doGet(api).pipe().subscribe({
      next: (response) => {
        this.editedUserDetails = response.data;
        this.imageSrc = this.editedUserDetails.profilePicture || '';

        this.userForm.controls['firstName'].setValue(this.editedUserDetails.firstName || '');
        this.userForm.controls['lastName'].setValue(this.editedUserDetails.lastName || '');
        this.userForm.controls['userEmail'].setValue(this.editedUserDetails.emailId || '');

        this.userForm.controls['phone'].setValue(this.editedUserDetails.phoneNo || '');
        this.userForm.controls['address'].setValue(this.editedUserDetails.address || '');
        this.imageSrc = this.editedUserDetails.profileImageUrl || '';
        this.userForm.get('userEmail')?.disable();
        this.spinner.hide();
        if (this.isPreviewTrue) {
          this.userForm.disable();
        }
      },
      error: (err) => {
        this.spinner.hide();
        console.error(err);
      }
    });
  }

  close() {
    this.dialogRef.close(false);
  }
}